import httpClient from './httpClient';

export interface UserDetails {
  uuid: string;
  email: string;
  role_id: number;
  first_name?: string;
  last_name?: string;
  contact?: string;
  gender?: 'male' | 'female' | 'other';
  dob?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  specialization?: string;
  expertise?: string;
  dp?: string;
  social_links?: any;
  about?: string;
  resume_url?: string;
  profile_visibility?: 'public' | 'private';
  batch_id?: number;
}

export interface UpdateUserDetailsData {
  first_name?: string;
  last_name?: string;
  contact?: string;
  gender?: 'male' | 'female' | 'other';
  dob?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  specialization?: string;
  expertise?: string;
  social_links?: any;
  about?: string;
  resume_url?: string;
  profile_visibility?: 'public' | 'private';
}

export interface UpdatePasswordData {
  currentPassword: string;
  newPassword: string;
}

/**
 * Service for managing user profile data
 */
class UserService {
  /**
   * Get own profile
   * @returns Promise<UserDetails>
   */
  async getOwnProfile(): Promise<UserDetails> {
    const response = await httpClient.get<UserDetails>('/users/me');
    return response;
  }

  /**
   * Get user by UUID
   * @param uuid - User UUID
   * @returns Promise<UserDetails>
   */
  async getUser(uuid: string): Promise<UserDetails> {
    const response = await httpClient.get<UserDetails>(`/users/${uuid}`);
    return response;
  }

  /**
   * Update user details
   * @param uuid - User UUID
   * @param data - Update data
   * @returns Promise<UserDetails>
   */
  async updateUserDetails(uuid: string, data: UpdateUserDetailsData): Promise<UserDetails> {
    const response = await httpClient.put<UserDetails>(`/users/${uuid}/details`, data);
    return response;
  }

  /**
   * Update password
   * @param uuid - User UUID
   * @param data - Password data
   * @returns Promise<void>
   */
  async updatePassword(uuid: string, data: UpdatePasswordData): Promise<void> {
    await httpClient.put(`/users/${uuid}/password`, data);
  }

  /**
   * Upload profile picture
   * @param uuid - User UUID
   * @param file - Image file
   * @returns Promise<UserDetails>
   */
  async uploadProfilePicture(uuid: string, file: File): Promise<UserDetails> {
    const formData = new FormData();
    formData.append('profile_picture', file);

    const response = await httpClient.put<UserDetails>(
      `/users/${uuid}/profile-picture`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response;
  }
}

export default new UserService();
